import React, { useState, useMemo } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { Icon } from '../../foundation/primitives';
import { JudgementModal, StartGate, FailOverlay, MinigameHud } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

const Template01Investigation = ({
  hostSubmit,
  hostConfig,
  hostMeta,
  registryId = 'minigame_investigation_01',
  templateId = 1
}) => {
  const [step, setStep] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [gameState, setGameState] = useState('playing');
  const [feedback, setFeedback] = useState(null);
  const [collectedItems, setCollectedItems] = useState(new Set());
  const [popupItem, setPopupItem] = useState(null);
  const [showEffect, setShowEffect] = useState(null);
  const [zoomTarget, setZoomTarget] = useState(null);
  const [pendingComplete, setPendingComplete] = useState(false);
  // R1: 開始ゲート（体験文法統一）。step.ui.showTutorial === false の時のみ最初から閉じる
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  // R1: 失敗理由（fail 既定文言の出し分け用）と、やり直し回数（全 submit payload に同梱）
  const [failReason, setFailReason] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);

  const defaultItems = [
    { id: 'window', label: '窓', description: '外の様子を確認できる窓。何か手がかりがあるかもしれない。', position: { top: '15%', left: '40%' } },
    { id: 'bookshelf', label: '本棚', description: '古い本が並んでいる。一冊だけ位置がずれている。', position: { top: '35%', left: '70%' } },
    { id: 'desk', label: '机', description: '作業机の上には書類が散らばっている。', position: { top: '60%', left: '25%' } },
    { id: 'plant', label: '観葉植物', description: '部屋の隅にある観葉植物。土が最近掘り返された跡がある。', position: { top: '50%', left: '65%' } },
  ];
  const defaultCorrectOrder = ['window', 'bookshelf', 'desk'];
  const configItems = Array.isArray(hostConfig?.items) ? hostConfig.items : null;
  const configCorrectOrder = Array.isArray(hostConfig?.correctOrder) ? hostConfig.correctOrder : null;
  const items = configItems && configItems.length > 0 ? configItems : defaultItems;
  const correctOrder = configCorrectOrder && configCorrectOrder.length > 0 ? configCorrectOrder : defaultCorrectOrder;
  const orderHintText = hostConfig?.orderHint?.text ?? 'ヒント: 「光 → 知識 → 手元」';
  const showHintAtStart = hostConfig?.orderHint?.showAtStart !== false;
  const showFoundItems = hostConfig?.showFoundItems !== false;
  const foundItemStyle = hostConfig?.foundItemStyle ?? 'checkmark';
  const backgroundStyle = useMinigameBackground(hostConfig);
  const showItemMarkers = hostConfig?.showItemMarkers !== false;
  const instructionText = hostConfig?.instructionText ?? '画面の気になるところをタップして調べてみよう';

  const mode = hostConfig?.mode === 'unordered' ? 'unordered' : 'ordered';

  // R0: BaseConfig の successMessage / failMessage / maxMistakes を consume する。
  // ホストは raw config を渡す（Zod default 充填なし）ため、不在時の既定値は現行デモの文言・上限3。
  const pickMessageText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);
  const successMessageConfig = typeof hostConfig?.successMessage === 'object' && hostConfig.successMessage !== null ? hostConfig.successMessage : {};
  const failMessageConfig = typeof hostConfig?.failMessage === 'object' && hostConfig.failMessage !== null ? hostConfig.failMessage : {};
  const successTitle = pickMessageText(successMessageConfig.title, '捜査完了！');
  const successDescription = pickMessageText(
    successMessageConfig.subtitle,
    mode === 'unordered' ? 'すべての手がかりを発見' : '正しい順番で証拠を発見'
  );
  const failTitle = pickMessageText(failMessageConfig.title, '捜査失敗...');
  // R1: 既定 subtitle は失敗理由で出し分け（敬体「ミスが多すぎました」は常体へ）
  const failDescription = pickMessageText(
    failMessageConfig.subtitle,
    failReason === 'timeout' ? '時間切れ' : 'ミスが多すぎた'
  );
  const allowRetry = failMessageConfig.allowRetry !== false;
  // maxMistakes: ordered モードのみ意味を持つ（現行仕様維持）。config 優先（R0 凍結の >=0 受理）
  // → hostMeta（step 側の値。step schema は 0 を「未設定」とするため >0 のみ採用）→ 既定3。
  // N 回目のミスで失敗＝許容ミスは N-1 回（既定3は従来の「3回で失敗」と同一挙動）。
  const maxMistakes = Number.isFinite(hostConfig?.maxMistakes) && hostConfig.maxMistakes >= 0
    ? Math.floor(hostConfig.maxMistakes)
    : Number.isFinite(hostMeta?.maxMistakes) && hostMeta.maxMistakes > 0
      ? Math.floor(hostMeta.maxMistakes)
      : 3;

  // R1 実動タイマー: config.timeLimitMs 優先 → hostMeta.timeLimitMs（step 側）。0 以下は
  // useMinigameTimer 側で無効化＝表示も強制も無し（ゼロ config 回帰安全）。
  // config.showTimer === false は「表示のみ」抑止で、満了 fail の強制は維持する。
  const limitMs = Number.isFinite(hostConfig?.timeLimitMs)
    ? hostConfig.timeLimitMs
    : Number.isFinite(hostMeta?.timeLimitMs)
      ? hostMeta.timeLimitMs
      : null;
  const showTimerChip = hostConfig?.showTimer !== false;
  // ゲート/HUD の題（旧・操作説明パネルの見出しと同じ mode 別タイトル）
  const hudTitle = mode === 'unordered' ? '現場捜索' : '順番推理';

  const targetItems = useMemo(() => {
    if (mode === 'unordered') {
      const targets = Array.isArray(hostConfig?.targetItems) ? hostConfig.targetItems : correctOrder;
      return new Set(targets);
    }
    return new Set(correctOrder);
  }, [mode, hostConfig?.targetItems, correctOrder]);

  // R1: ゲート表示中・成功確定待ち（pendingComplete）・決着後は止める。満了で fail 状態へ
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs,
    running: !gateOpen && gameState === 'playing' && !pendingComplete,
    onExpire: () => {
      if (gameState !== 'playing' || pendingComplete) return;
      setFailReason('timeout');
      setGameState('fail');
    }
  });

  const handleTap = (id) => {
    if (gameState !== 'playing') return;

    const item = items.find(i => i.id === id);

    if (mode === 'unordered') {
      if (collectedItems.has(id)) {
        if (item?.description) {
          setPopupItem(item);
        }
        return;
      }

      if (targetItems.has(id)) {
        const newCollected = new Set(collectedItems);
        newCollected.add(id);
        setCollectedItems(newCollected);
        setFeedback({ type: 'success', message: '発見！' });
        setShowEffect('discover');
        setZoomTarget(id);

        setTimeout(() => setZoomTarget(null), 400);

        if (item?.description) {
          setTimeout(() => {
            setFeedback(null);
            setShowEffect(null);
            setPopupItem(item);
          }, 500);
        } else {
          setTimeout(() => {
            setFeedback(null);
            setShowEffect(null);
          }, 800);
        }

        if (newCollected.size >= targetItems.size) {
          if (item?.description) {
            setPendingComplete(true);
          } else {
            setTimeout(() => {
              setShowEffect('success');
              setGameState('success');
            }, 800);
          }
        }
      } else {
        if (item?.description) {
          setPopupItem(item);
        }
      }
    } else {
      if (correctOrder[step] === id) {
        setFeedback({ type: 'success', message: '正解！' });
        setShowEffect('discover');
        setZoomTarget(id);

        setTimeout(() => setZoomTarget(null), 400);

        if (item?.description) {
          setTimeout(() => {
            setFeedback(null);
            setShowEffect(null);
            setPopupItem(item);
          }, 500);
        } else {
          setTimeout(() => {
            setFeedback(null);
            setShowEffect(null);
          }, 800);
        }

        if (step === correctOrder.length - 1) {
          if (item?.description) {
            setPendingComplete(true);
          } else {
            setTimeout(() => {
              setShowEffect('success');
              setGameState('success');
            }, 800);
          }
        } else {
          setStep(step + 1);
        }
      } else {
        setFeedback({ type: 'error', message: 'ミス...' });
        setShowEffect('fail');
        setMistakes(mistakes + 1);
        if (mistakes >= maxMistakes - 1) {
          setFailReason('mistakes');
          setTimeout(() => {
            setGameState('fail');
          }, 500);
        }
        setTimeout(() => {
          setFeedback(null);
          setShowEffect(null);
        }, 800);
      }
    }
  };

  const closePopup = () => {
    setPopupItem(null);
    if (pendingComplete) {
      setPendingComplete(false);
      setTimeout(() => {
        setShowEffect('success');
        setGameState('success');
      }, 300);
    }
  };

  const reset = () => {
    setStep(0);
    setMistakes(0);
    setGameState('playing');
    setFeedback(null);
    setCollectedItems(new Set());
    setPopupItem(null);
    setShowEffect(null);
    setZoomTarget(null);
    setPendingComplete(false);
    setFailReason(null);
    resetSubmission();
  };

  const progress = mode === 'unordered' ? collectedItems.size : step;
  const total = mode === 'unordered' ? targetItems.size : correctOrder.length;

  return (
    <div className="t01-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {!backgroundStyle && <div className="t01-bg-overlay absolute inset-0" />}

      {/* R1: 共通 HUD（旧・偽タイマー「03:00」固定チップ＋ミスチップ＋飾りメニューを撤去して置換）。
          タイマーは実動時のみ表示（showTimer=false は表示のみ抑止・満了 fail は維持）。
          ミスチップは ordered モードのみ（現行どおり）。?＝開始ゲート再表示 */}
      <MinigameHud
        title={hudTitle}
        icon="Search"
        timer={showTimerChip && remainingMs != null ? { remainingMs, totalMs: limitMs } : null}
        mistakes={mode === 'ordered' ? { current: mistakes, max: maxMistakes } : null}
        onHelp={() => setGateOpen(true)}
      />

      <div className="absolute inset-0">
        {items.map((item) => {
          const isCollected = mode === 'unordered'
            ? collectedItems.has(item.id)
            : correctOrder.indexOf(item.id) < step;
          const isTarget = mode === 'unordered'
            ? targetItems.has(item.id)
            : correctOrder.includes(item.id);

          const showSubtleGlow = item.glowEffect === true && !isCollected;
          const markerClass = showItemMarkers
            ? (isCollected && showFoundItems
                ? 't01-marker--found ring-2'
                : isTarget
                  ? 't01-marker--target'
                  : 't01-marker--idle')
            : (isCollected && showFoundItems
                ? 't01-marker--found-soft ring-2'
                : showSubtleGlow
                  ? 't01-marker--subtle-glow'
                  : '');

          const isZoomed = zoomTarget === item.id;
          const pulseClass = showItemMarkers && isTarget && !isCollected ? 'animate-pulse' : '';

          return (
            <div
              key={item.id}
              className={`absolute rounded-xl cursor-pointer flex items-center justify-center ${markerClass} ${pulseClass}`}
              style={{
                top: item.position?.top ?? item.position?.y,
                left: item.position?.left ?? item.position?.x,
                width: item.size?.width ?? '64px',
                height: item.size?.height ?? '64px',
                transform: `translate(-50%, -50%) scale(${isZoomed ? 1.3 : 1})`,
                transition: 'transform 0.3s ease-out, background-color 0.2s',
                zIndex: isZoomed ? 100 : 1
              }}
              onClick={() => handleTap(item.id)}
            >
              {isCollected && showFoundItems && foundItemStyle === 'checkmark' && <Icon name="CircleCheck" size={32} className="t01-marker-check" />}
              {showItemMarkers && (
                <span className="t01-marker-label absolute -bottom-6 text-xs whitespace-nowrap">{item.label}</span>
              )}
              {isZoomed && (
                <div className="t01-marker-ping absolute inset-0 rounded-xl ring-4 animate-ping" style={{ animationDuration: '0.5s' }} />
              )}
            </div>
          );
        })}
      </div>

      {feedback && !popupItem && (
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 px-6 py-3 rounded-xl font-bold text-lg
          ${feedback.type === 'success' ? 't01-feedback--success' : 't01-feedback--error'}`}>
          {feedback.message}
        </div>
      )}

      {popupItem && gameState === 'playing' && (
        <div className="t01-popup-overlay absolute inset-0 flex items-center justify-center p-4" style={{ zIndex: 200 }} onClick={closePopup}>
          <div
            className="t01-popup-panel border rounded-2xl p-5 max-w-[280px] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="t01-popup-icon-badge w-8 h-8 rounded-lg flex items-center justify-center">
                <Icon name="Eye" size={20} className="t01-popup-icon" />
              </div>
              <h3 className="t01-popup-title font-bold text-lg">{popupItem.label}</h3>
            </div>
            <p className="t01-popup-desc text-sm leading-relaxed mb-4">{popupItem.description}</p>
            <button
              className="t01-popup-close w-full font-bold py-2 px-4 rounded-lg transition-colors"
              onClick={closePopup}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {/* R1: 旧・操作説明パネル＋「操作」トグルは撤去（ルール説明は StartGate に集約し、
          HUD の ? で随時再表示）。下部は進捗ピルのみ残す（HUD progress とは二重にしない） */}
      <div className="absolute bottom-0 left-0 right-0 z-30 px-4 pb-4">
        <div className="t01-progress-chip flex items-center gap-2 backdrop-blur-sm rounded-full px-3 py-1.5">
          <div className="t01-progress-track flex-1 h-1.5 rounded-full overflow-hidden">
            <div className="t01-progress-fill h-full transition-all" style={{ width: `${(progress / total) * 100}%` }} />
          </div>
          <span className="t01-progress-count text-xs font-mono">{progress}/{total}</span>
        </div>
      </div>

      {/* Foundation: 捜査完了モーダル（中央バッジ⭐ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          文言は config.successMessage（title/subtitle）を優先し、不在時は現行文言（説明は mode で切替）。
          旧: amber グラデ円 Star バッジ + 独自 bg-black/85 オーバーレイ + ChevronRight。
          R1: payload に retryCount を同梱） */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={successTitle}
        description={successDescription}
        icon={<Icon name="Star" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />

      {/* R1: 失敗は semantic FailOverlay に統一（旧 .t01-fail-overlay と「失敗として送信」の
          システム語を撤去）。文言は config.failMessage（title/subtitle/allowRetry）を優先し、
          subtitle 既定は失敗理由で出し分け。やり直すで retryCount を加算してタイマーも再アーム。
          未整理のまま進む → submit('fail')（payload に retryCount 同梱） */}
      {gameState === 'fail' && (
        <FailOverlay
          title={failTitle}
          subtitle={failDescription}
          allowRetry={allowRetry}
          onRetry={() => {
            setRetryCount((count) => count + 1);
            reset();
            resetTimer();
          }}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* R1: 開始ゲート（体験文法統一）。開始までゲーム操作・タイマーは始まらない。
          HUD の ? で随時再表示（表示中はタイマー一時停止）。instruction は config の
          instructionText（既定文言あり）＋ ordered 時の順番ヒント（showAtStart 尊重） */}
      {gateOpen && (
        <StartGate
          title={hudTitle}
          icon="Search"
          instruction={
            mode === 'ordered' && showHintAtStart ? (
              <>
                <p className="m-0">{instructionText}</p>
                <p className="t01-gate-hint m-0 mt-1 text-xs">{orderHintText}</p>
              </>
            ) : (
              instructionText
            )
          }
          onStart={() => setGateOpen(false)}
        />
      )}

      {showEffect === 'discover' && (
        <SuccessEffect type="shine" duration={400} onComplete={() => {}} />
      )}
      {showEffect === 'success' && (
        <SuccessEffect type="star" duration={1000} onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'fail' && (
        <FailureEffect type="shake" duration={400} onComplete={() => {}} />
      )}
    </div>
  );
};

export default Template01Investigation;
